import React, { useEffect, useMemo, useState } from "react";
import api, { downloadFile } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import {
  DomainUser,
  CommandQueuedResponse,
  AuditEntry,
  OffboardOptions,
  ADUpdateRequest,
} from "../types";
import { POLICY_HINT, checkPassword } from "../utils/passwordPolicy";
import { usePublishScreenData } from "../utils/aiScreenContext";
import ADGroups from "../components/ADGroups";
import IdentityAssistPanel from "../components/IdentityAssistPanel";
import OnboardUserModal from "../components/OnboardUserModal";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";
import { formatDateTime } from "../utils/datetime";

const DEFAULT_OFFBOARD: OffboardOptions = {
  disable_account: true,
  reset_password: true,
  revoke_sessions: true,
  move_to_disabled_ou: true,
  strip_group_memberships: true,
  note: "",
};

const EDIT_FIELDS: { key: keyof ADUpdateRequest; label: string; wide?: boolean }[] = [
  { key: "display_name", label: "Display name" },
  { key: "given_name", label: "First name" },
  { key: "surname", label: "Last name" },
  { key: "email", label: "Email" },
  { key: "user_principal_name", label: "User logon (UPN)" },
  { key: "title", label: "Title" },
  { key: "department", label: "Department" },
  { key: "company", label: "Company" },
  { key: "office", label: "Office" },
  { key: "office_phone", label: "Office phone" },
  { key: "mobile_phone", label: "Mobile phone" },
  { key: "mail_nickname", label: "Mail nickname (alias)" },
  { key: "description", label: "Description", wide: true },
];

/** Edit-all-attributes modal. Pre-fills from the synced DomainUser, sends only
 *  changed fields to PATCH /api/v1/ad/users/{id} (empty string clears an attr).
 *  Modeled on the page's other modals; z-50 to clear the sidebar. */
function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: DomainUser;
  onClose: () => void;
  onSaved: (msg: string, commandId?: number) => void;
}) {
  const initial = useMemo(() => {
    const o: Record<string, string> = {};
    EDIT_FIELDS.forEach((f) => {
      o[f.key] = ((user as unknown as Record<string, string>)[f.key] as string) ?? "";
    });
    return o;
  }, [user]);
  const [form, setForm] = useState<Record<string, string>>(initial);
  const initialAliases = useMemo(() => (user.proxy_addresses || []).join("\n"), [user]);
  const [aliasesText, setAliasesText] = useState(initialAliases);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const changed = EDIT_FIELDS.filter((f) => (form[f.key] ?? "") !== (initial[f.key] ?? ""));
  const aliasesChanged = aliasesText !== initialAliases;
  const changeCount = changed.length + (aliasesChanged ? 1 : 0);

  const save = async () => {
    if (changeCount === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setErr("");
    const body: ADUpdateRequest = {};
    changed.forEach((f) => {
      (body as Record<string, string>)[f.key] = form[f.key] ?? "";
    });
    if (aliasesChanged) {
      body.proxy_addresses = aliasesText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    const labels = [
      ...changed.map((c) => c.label.toLowerCase()),
      ...(aliasesChanged ? ["aliases"] : []),
    ];
    try {
      const r = await api.patch(`/api/v1/ad/users/${user.id}`, body);
      // Hand the command id up so the page can WATCH it and refresh when the
      // runner has actually applied it. Without that the list keeps showing
      // the old value indefinitely - reported as "AG still isn't updating
      // promptly after changes in AG".
      onSaved(
        `Update queued for ${user.sam_account_name} (${labels.join(", ")}). Waiting for the AD runner…`,
        r.data?.command_id,
      );
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Failed to queue the update.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Edit AD user — <code className="text-base">{user.sam_account_name}</code>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Only changed fields are sent. Clearing a field removes that attribute in AD. Changes
            queue for the customer's AD runner and apply on its next check-in.
          </p>
          {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {EDIT_FIELDS.map((f) => (
              <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {f.label}
                </label>
                <input
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Email aliases (proxyAddresses) — one per line
            </label>
            <textarea
              value={aliasesText}
              onChange={(e) => setAliasesText(e.target.value)}
              rows={4}
              placeholder={"SMTP:primary@company.com\nsmtp:alias@company.com"}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm font-mono"
            />
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
              Uppercase <code>SMTP:</code> = primary address; lowercase <code>smtp:</code> = alias.
              Saving replaces the full alias set (this is what Azure AD Connect syncs). Requires the
              runner agent at 1.0.36+.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || changeCount === 0}
            className="text-sm px-4 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving
              ? "Saving…"
              : changeCount
                ? `Save ${changeCount} change${changeCount > 1 ? "s" : ""}`
                : "No changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ADUsersProps {
  /** When supplied, the customer dropdown is hidden and the page is locked to this customer.
   *  Used to embed this page as a tab on the customer-detail view. */
  customerOverride?: number | null;
  /** Hide the page title (set when embedded in a tab that already has its own heading). */
  hideTitle?: boolean;
}

/** Accounts Get-ADUser returns that are not people.
 *
 *  A trailing "$" is a computer or inter-domain trust account. The rest are
 *  service principals created by Exchange or Entra Connect. Administrator and
 *  Guest are deliberately NOT here: they are real accounts an admin expects to
 *  see, even though they carry no attributes.
 */
function isServiceAccount(sam?: string | null): boolean {
  if (!sam) return false;
  const n = sam.trim();
  if (n.endsWith("$")) return true;
  const lower = n.toLowerCase();
  return (
    lower === "krbtgt" ||
    lower.startsWith("msol_") ||
    lower.startsWith("aad_") ||
    lower.startsWith("healthmailbox") ||
    lower.startsWith("systemmailbox") ||
    lower.startsWith("discoverysearchmailbox") ||
    lower.startsWith("sm_") ||
    lower.startsWith("$")
  );
}

export default function ADUsers({ customerOverride = null, hideTitle = false }: ADUsersProps) {
  const { user } = useAuth();
  const canAct = user?.role === "admin" || user?.role === "technician";
  const isAdmin = user?.role === "admin";
  const embedded = customerOverride != null;

  const { currentCustomerId } = useCustomer();
  // Non-embedded: follow the global customer switcher (no local picker). Embedded
  // (customer-detail tab): locked to the customer passed in via customerOverride.
  const customerId = embedded ? customerOverride : currentCustomerId;
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [users, setUsers] = useState<DomainUser[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "enabled" | "disabled">("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [syncMsg, setSyncMsg] = useState("");

  const [offboardTarget, setOffboardTarget] = useState<DomainUser | null>(null);
  const [offboardOpts, setOffboardOpts] = useState<OffboardOptions>(DEFAULT_OFFBOARD);
  // Matched Microsoft 365 account (by UPN) so offboard can lock the user out of
  // both directories in one flow.
  const [m365Match, setM365Match] = useState<any | null>(null);
  const [m365Off, setM365Off] = useState({
    disable: true,
    revoke_sessions: true,
    reset_mfa: true,
    remove_licenses: true,
  });
  const [editTarget, setEditTarget] = useState<DomainUser | null>(null);
  // Which user's full attribute set is open. The table only ever showed five
  // columns, so UPN, email, department, office, phones, company and the OU were
  // synced and then invisible - which is why the attributes looked unpopulated.
  const [expandedUser, setExpandedUser] = useState<number | null>(null);
  // Trust and service accounts are hidden by default: they have no
  // attributes by nature, and sorted by name they filled the first screen
  // with rows of dashes, which read as "nothing synced".
  const [hideServiceAccounts, setHideServiceAccounts] = useState(true);
  const [resetTarget, setResetTarget] = useState<DomainUser | null>(null);
  const [resetMode, setResetMode] = useState<"auto" | "custom">("auto");
  const [resetCustomPw, setResetCustomPw] = useState("");
  const [resetCustomConfirm, setResetCustomConfirm] = useState("");
  const [resetError, setResetError] = useState("");

  const [passwordResult, setPasswordResult] = useState<{
    sam: string;
    password: string;
    action: string;
  } | null>(null);
  const [actionToast, setActionToast] = useState<string>("");
  const userSort = useSort("display_name");
  const auditSort = useSort("created_at", "desc");

  const loadUsers = async () => {
    if (!customerId) return;
    setLoading(true);
    setError("");
    try {
      const params: any = { customer_id: customerId };
      if (search) params.search = search;
      if (statusFilter !== "all") params.enabled = statusFilter === "enabled";
      const r = await api.get("/api/v1/ad/users", { params });
      setUsers(r.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load AD users");
    } finally {
      setLoading(false);
    }
  };

  const loadAudit = async () => {
    if (!customerId) return;
    try {
      const r = await api.get("/api/v1/ad/audit", {
        params: { customer_id: customerId, limit: 25 },
      });
      setAudit(r.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadUsers();
    loadAudit(); /* eslint-disable-next-line */
  }, [customerId, statusFilter]);

  const triggerSync = async () => {
    if (!customerId) return;
    setSyncMsg("");
    try {
      await api.post(`/api/v1/ad/sync/${customerId}`);
      setSyncMsg("Sync queued — refresh in a minute once the AD runner picks it up.");
      loadAudit();
    } catch (err: any) {
      setSyncMsg(err.response?.data?.detail || "Sync failed to queue");
    }
  };

  const showToast = (msg: string) => {
    setActionToast(msg);
    setTimeout(() => setActionToast(""), 5000);
  };

  /* WATCH A QUEUED AD COMMAND, then re-read from the server.
   *
   * REPORTED: "AG still isn't updating promptly after changes in AG. It
   * updated on AD and through O365 but still shows outdated data in AG."
   *
   * Two halves were missing. The server did not apply a finished write to
   * domain_users (fixed in services/ad_writeback.py), and this page never
   * looked again: an attribute edit showed "queued" and then nothing, so
   * the operator watched the old display name sit there and went and
   * renamed it in AD directly.
   *
   * Polling rather than patching optimistically, because the point is to
   * show what ACTUALLY happened. A failed command now says so instead of
   * leaving a change on screen that never landed.
   *
   * The runner picks work up within ~20s, so 90s of 3s polls covers a
   * normal apply without holding a timer open all afternoon. Giving up is
   * explicit - it says the command is still queued rather than silently
   * stopping. */
  const watchCommand = (commandId: number | undefined, label: string) => {
    if (!commandId) {
      loadUsers();
      return;
    }
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await api.get(`/api/v1/ad/commands/${commandId}`);
        const st = r.data?.status;
        if (st === "completed") {
          await loadUsers();
          showToast(`${label} applied.`);
          return;
        }
        if (st === "failed") {
          await loadUsers();
          showToast(`${label} FAILED: ${r.data?.error_message || "the runner reported an error"}`);
          return;
        }
      } catch {
        /* a transient read failure is not an outcome; keep waiting */
      }
      if (tries >= 30) {
        showToast(`${label} is still queued — the AD runner has not picked it up yet.`);
        return;
      }
      window.setTimeout(tick, 3000);
    };
    window.setTimeout(tick, 3000);
  };

  // Optimistically flip a row in local state so the UI reflects the queued action
  // immediately. The watcher above confirms it against the server, or corrects
  // it if the runner reported a failure.
  const patchUserLocally = (id: number, patch: Partial<DomainUser>) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const doDisable = async (du: DomainUser) => {
    if (!window.confirm(`Disable AD account "${du.sam_account_name}"?`)) return;
    try {
      const cmd = await api.post<CommandQueuedResponse>(`/api/v1/ad/users/${du.id}/disable`);
      patchUserLocally(du.id, { enabled: false });
      watchCommand(cmd.data?.command_id, `Disable of ${du.sam_account_name}`);
      showToast(`Disable queued for ${du.sam_account_name}. Agent will execute on next check-in.`);
      loadAudit();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to queue disable");
    }
  };

  const doEnable = async (du: DomainUser) => {
    if (!window.confirm(`Re-enable AD account "${du.sam_account_name}"?`)) return;
    try {
      const cmd = await api.post<CommandQueuedResponse>(`/api/v1/ad/users/${du.id}/enable`);
      patchUserLocally(du.id, { enabled: true });
      watchCommand(cmd.data?.command_id, `Re-enable of ${du.sam_account_name}`);
      showToast(
        `Re-enable queued for ${du.sam_account_name}. Agent will execute on next check-in.`,
      );
      loadAudit();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to queue enable");
    }
  };

  const doUnlock = async (du: DomainUser) => {
    try {
      const cmd = await api.post<CommandQueuedResponse>(`/api/v1/ad/users/${du.id}/unlock`);
      patchUserLocally(du.id, { locked: false });
      watchCommand(cmd.data?.command_id, `Unlock of ${du.sam_account_name}`);
      showToast(`Unlock queued for ${du.sam_account_name}. Agent will execute on next check-in.`);
      loadAudit();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to queue unlock");
    }
  };

  const closeResetModal = () => {
    setResetTarget(null);
    setResetMode("auto");
    setResetCustomPw("");
    setResetCustomConfirm("");
    setResetError("");
  };

  const confirmReset = async () => {
    if (!resetTarget) return;
    setResetError("");
    let body: { new_password?: string } = {};
    if (resetMode === "custom") {
      const pw = resetCustomPw.trim();
      // Same rules the server applies, so the reason is specific and arrives
      // before the round trip.
      const problem = checkPassword(pw, { username: resetTarget?.sam_account_name });
      if (problem) {
        setResetError(problem);
        return;
      }
      if (pw !== resetCustomConfirm.trim()) {
        setResetError("Passwords don't match");
        return;
      }
      body.new_password = pw;
    }
    try {
      const r = await api.post<CommandQueuedResponse>(
        `/api/v1/ad/users/${resetTarget.id}/reset-password`,
        body,
      );
      if (r.data.generated_password) {
        setPasswordResult({
          sam: resetTarget.sam_account_name,
          password: r.data.generated_password,
          action: resetMode === "custom" ? "Password reset (custom)" : "Password reset (generated)",
        });
      }
      showToast(
        `Password reset queued for ${resetTarget.sam_account_name}. Agent will apply on next check-in.`,
      );
      closeResetModal();
      loadAudit();
    } catch (err: any) {
      setResetError(err.response?.data?.detail || "Failed to reset password");
    }
  };

  // When the offboard modal opens, look up the matching Microsoft 365 account
  // by UPN/email so we can offer to offboard both directories together.
  useEffect(() => {
    if (!offboardTarget || !customerId) {
      setM365Match(null);
      return;
    }
    const upn = offboardTarget.user_principal_name || offboardTarget.email;
    if (!upn) {
      setM365Match(null);
      return;
    }
    let cancelled = false;
    api
      .get("/api/v1/m365/user-lookup", { params: { customer_id: customerId, upn } })
      .then((r) => {
        if (!cancelled) setM365Match(r.data?.found ? r.data : null);
      })
      .catch(() => {
        if (!cancelled) setM365Match(null);
      });
    return () => {
      cancelled = true;
    };
  }, [offboardTarget, customerId]);

  const confirmOffboard = async () => {
    if (!offboardTarget) return;
    const summary: string[] = [];
    try {
      const r = await api.post<CommandQueuedResponse>(
        `/api/v1/ad/users/${offboardTarget.id}/offboard`,
        offboardOpts,
      );
      if (r.data.generated_password) {
        setPasswordResult({
          sam: offboardTarget.sam_account_name,
          password: r.data.generated_password,
          action: "Offboard",
        });
      }
      if (offboardOpts.disable_account) {
        patchUserLocally(offboardTarget.id, { enabled: false });
      }
      summary.push("AD offboard queued");
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to queue AD offboard");
      return;
    }
    // Microsoft 365 half — synchronous Graph actions on the matched account.
    const anyM365 =
      m365Off.disable || m365Off.revoke_sessions || m365Off.reset_mfa || m365Off.remove_licenses;
    if (m365Match && anyM365) {
      try {
        await api.post(`/api/v1/m365/users/${m365Match.id}/offboard`, m365Off);
        summary.push("Microsoft 365 offboarded");
      } catch (err: any) {
        summary.push(`M365 offboard failed: ${err.response?.data?.detail || "error"}`);
      }
    }
    showToast(`${summary.join("; ")}. AD steps run on the agent's next check-in.`);
    setOffboardTarget(null);
    setOffboardOpts(DEFAULT_OFFBOARD);
    setM365Match(null);
    loadAudit();
  };

  const sortedUsers = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return users;
    return users.filter(
      (u) =>
        u.sam_account_name?.toLowerCase().includes(term) ||
        u.display_name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.user_principal_name?.toLowerCase().includes(term),
    );
  }, [users, search]);

  const visibleUsers = hideServiceAccounts
    ? sortedUsers.filter((u) => !isServiceAccount(u.sam_account_name))
    : sortedUsers;
  const hiddenCount = sortedUsers.length - visibleUsers.length;

  const displayUsers = sortRows(visibleUsers, userSort.sort, (u, key) => {
    switch (key) {
      case "sam_account_name":
        return (u.sam_account_name || "").toLowerCase();
      case "title":
        return (u.title || "").toLowerCase();
      case "enabled":
        return u.enabled ? 1 : 0;
      case "last_logon":
        return u.last_logon ? Date.parse(u.last_logon) : 0;
      default:
        return (u.display_name || "").toLowerCase();
    }
  });

  usePublishScreenData(
    "Active Directory users",
    visibleUsers.map((u: any) => ({
      sam: u.sam_account_name,
      name: u.display_name,
      enabled: u.enabled,
      locked: u.locked,
      last_logon: u.last_logon,
    })),
    hideServiceAccounts ? "service accounts hidden" : undefined,
  );

  const sortedAudit = sortRows(audit, auditSort.sort, (a, key) => {
    switch (key) {
      case "action":
        return (a.action || "").toLowerCase();
      case "target_label":
        return (a.target_label || "").toLowerCase();
      case "actor_username":
        return (a.actor_username || "").toLowerCase();
      case "result":
        return (a.result || "").toLowerCase();
      default:
        return a.created_at ? Date.parse(a.created_at) : 0;
    }
  });

  // AD is inherently per-customer (the runner lives at one customer's site), so
  // "All customers" can't render a directory. Prompt the user to pick one.
  if (!embedded && !customerId) {
    return (
      <div>
        {!hideTitle && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Active Directory</h1>}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-10 text-center text-gray-500 dark:text-gray-400">
          Select a customer from the switcher at the top to manage its Active Directory users.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        {!hideTitle && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Active Directory</h1>}
        <div className={`flex items-center gap-2 ${hideTitle ? "ml-auto" : ""}`}>
          {canAct && (
            <button
              onClick={() => setOnboardOpen(true)}
              className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-blue-700"
            >
              + Onboard user
            </button>
          )}
          {canAct && (
            <ActionButton onAction={() => triggerSync()} variant="raw" className="border dark:border-gray-600 dark:text-gray-100 text-sm px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Syncing…">
              Sync now
            </ActionButton>
          )}
          {isAdmin && customerId && (
            <button
              onClick={() =>
                downloadFile(`/api/v1/exports/ad-audit/${customerId}.pdf`, "ad-audit.pdf", {
                  days: 90,
                })
              }
              className="border dark:border-gray-600 dark:text-gray-100 text-sm px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
              title="Admin only: PDF of every AD action taken against this customer's directory"
            >
              Audit PDF
            </button>
          )}
        </div>
      </div>

      {!embedded && <IdentityAssistPanel customerId={customerId} scope="ad" />}

      {syncMsg && <div className="mb-3 text-xs text-blue-600 dark:text-blue-400">{syncMsg}</div>}
      {error && <div className="mb-3 text-sm text-red-500 dark:text-red-400">{error}</div>}
      {actionToast && (
        <div className="mb-3 text-sm bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800 px-4 py-2 rounded-lg flex items-center gap-2">
          <span>✓</span>
          {actionToast}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-4 py-3 border-b dark:border-gray-700 flex items-center gap-2">
          <input
            placeholder="Search by username, name, email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") loadUsers();
            }}
            className="flex-1 border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
          <ActionButton onAction={() => loadUsers()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Loading…">
            Search
          </ActionButton>
          <label
            className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap cursor-pointer"
            title="Trust accounts (ending in $) and service principals created by Exchange or Entra Connect. They have no attributes by nature."
          >
            <input
              type="checkbox"
              checked={hideServiceAccounts}
              onChange={(e) => setHideServiceAccounts(e.target.checked)}
            />
            Hide service &amp; trust accounts
            {hiddenCount > 0 && hideServiceAccounts && (
              <span className="text-gray-400">({hiddenCount} hidden)</span>
            )}
          </label>
        </div>

        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th className="w-6" />
              <SortableTh label="Name" sortKey="display_name" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Username" sortKey="sam_account_name" sort={userSort.sort} onSort={userSort.toggle} />
              {/* UPN and Email were synced all along and shown nowhere, which is
                  why the attributes looked unpopulated. */}
              <SortableTh label="UPN" sortKey="user_principal_name" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Email" sortKey="email" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Title" sortKey="title" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Status" sortKey="enabled" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Last Logon" sortKey="last_logon" sort={userSort.sort} onSort={userSort.toggle} />
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && displayUsers.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400 dark:text-gray-500">
                  No AD users synced yet for this customer. Make sure a domain-joined device is set
                  as the AD runner, then click "Sync now".
                </td>
              </tr>
            )}
            {displayUsers.map((u) => (
              <>
              <tr
                key={u.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
              >
                <td className="pl-3 text-gray-400 select-none">{expandedUser === u.id ? "▾" : "▸"}</td>
                <td className="px-4 py-2 font-medium">{u.display_name || "—"}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{u.sam_account_name}</td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300 max-w-[16rem] truncate" title={u.user_principal_name || ""}>
                  {u.user_principal_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
                <td className="px-4 py-2 text-gray-600 dark:text-gray-300 max-w-[14rem] truncate" title={u.email || ""}>
                  {u.email || <span className="text-gray-300 dark:text-gray-600">—</span>}
                </td>
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{u.title || "—"}</td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${u.enabled ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}
                  >
                    {u.enabled ? "Enabled" : "Disabled"}
                  </span>
                  {u.locked && (
                    <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">
                      Locked
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {u.last_logon ? formatDateTime(u.last_logon) : "Never"}
                </td>
                <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                  {canAct && (
                    <button
                      onClick={() => setEditTarget(u)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      Edit
                    </button>
                  )}
                  {canAct && u.enabled && (
                    <button
                      onClick={() => doDisable(u)}
                      className="text-xs text-yellow-600 hover:underline"
                    >
                      Disable
                    </button>
                  )}
                  {canAct && !u.enabled && (
                    <button
                      onClick={() => doEnable(u)}
                      className="text-xs text-emerald-600 hover:underline font-medium"
                    >
                      Re-enable
                    </button>
                  )}
                  {canAct && (
                    <button
                      onClick={(e) => { e.stopPropagation(); doUnlock(u); }}
                      disabled={!u.locked}
                      title={u.locked ? "Unlock this locked-out account" : "Account is not locked"}
                      className={`text-xs ${
                        u.locked
                          ? "text-amber-600 hover:underline font-medium"
                          : "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                      }`}
                    >
                      Unlock
                    </button>
                  )}
                  {canAct && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setResetTarget(u); }}
                      className="text-xs text-blue-500 hover:underline"
                    >
                      Reset Password
                    </button>
                  )}
                  {canAct && u.enabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOffboardTarget(u);
                        setOffboardOpts(DEFAULT_OFFBOARD);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Offboard
                    </button>
                  )}
                </td>
              </tr>
              {expandedUser === u.id && (
                <tr key={`${u.id}-detail`} className="bg-gray-50/70 dark:bg-gray-900/40">
                  <td />
                  <td colSpan={8} className="px-4 py-3">
                    <div className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3 text-xs">
                      <Attr label="User principal name" value={u.user_principal_name} />
                      <Attr label="Email" value={u.email} />
                      <Attr label="First name" value={u.given_name} />
                      <Attr label="Surname" value={u.surname} />
                      <Attr label="Job title" value={u.title} />
                      <Attr label="Department" value={u.department} />
                      <Attr label="Company" value={u.company} />
                      <Attr label="Office" value={u.office} />
                      <Attr label="Office phone" value={u.office_phone} />
                      <Attr label="Mobile" value={u.mobile_phone} />
                      <Attr label="Exchange alias" value={u.mail_nickname} />
                      <Attr label="Created" value={u.when_created ? formatDateTime(u.when_created) : null} />
                      <Attr
                        label="Email aliases"
                        value={u.proxy_addresses?.length ? u.proxy_addresses.join(", ") : null}
                        wide
                      />
                      <Attr label="Organisational unit" value={ouOf(u.distinguished_name)} wide />
                    </div>
                    <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                      A dash means the field is empty in Active Directory, not that it failed to
                      sync. Most small domains never fill in job title or department.
                    </p>
                  </td>
                </tr>
              )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {canAct && editTarget && (
        <EditUserModal
          user={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(m, commandId) => {
            const who = editTarget.sam_account_name;
            setEditTarget(null);
            showToast(m);
            loadAudit();
            watchCommand(commandId, `Update to ${who}`);
          }}
        />
      )}

      {/* Groups. Placed between the user list and the audit log because
          membership is the other half of the same question — you look at a
          person, then at what they can reach. */}
      <div className="mb-6">
        <ADGroups customerId={customerId} />
      </div>

      {/* Audit log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recent AD activity</h2>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="When" sortKey="created_at" sort={auditSort.sort} onSort={auditSort.toggle} />
                <SortableTh label="Action" sortKey="action" sort={auditSort.sort} onSort={auditSort.toggle} />
                <SortableTh label="Target" sortKey="target_label" sort={auditSort.sort} onSort={auditSort.toggle} />
                <SortableTh label="By" sortKey="actor_username" sort={auditSort.sort} onSort={auditSort.toggle} />
                <SortableTh label="Result" sortKey="result" sort={auditSort.sort} onSort={auditSort.toggle} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {audit.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-gray-400 dark:text-gray-500">
                    No activity yet.
                  </td>
                </tr>
              )}
              {sortedAudit.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {formatDateTime(a.created_at)}
                  </td>
                  <td className="px-3 py-2 font-mono">{a.action}</td>
                  <td className="px-3 py-2">{a.target_label || "—"}</td>
                  <td className="px-3 py-2">{a.actor_username || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-0.5 rounded-full ${
                        a.result === "success"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                          : a.result === "failed"
                            ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {a.result}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset password confirm */}
      {resetTarget && (
        <Modal title="Reset AD password" onClose={closeResetModal}>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Reset the AD password for <strong>{resetTarget.sam_account_name}</strong>. The user will
            be required to change it at their next logon either way.
          </p>
          <div className="space-y-2 mb-4">
            <label
              className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              style={{ borderColor: resetMode === "auto" ? "#2563eb" : "#e5e7eb" }}
            >
              <input
                type="radio"
                name="resetMode"
                value="auto"
                checked={resetMode === "auto"}
                onChange={() => setResetMode("auto")}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Auto-generate a strong password</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  20 characters, mixed case + digits + symbols. Shown once.
                </div>
              </div>
            </label>
            <label
              className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              style={{ borderColor: resetMode === "custom" ? "#2563eb" : "#e5e7eb" }}
            >
              <input
                type="radio"
                name="resetMode"
                value="custom"
                checked={resetMode === "custom"}
                onChange={() => setResetMode("custom")}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Set a custom password</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {POLICY_HINT}
                </div>
                {resetMode === "custom" && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      autoComplete="new-password"
                      value={resetCustomPw}
                      onChange={(e) => setResetCustomPw(e.target.value)}
                      placeholder="New password"
                      className="w-full border rounded-lg px-3 py-2 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    />
                    <input
                      type="text"
                      autoComplete="new-password"
                      value={resetCustomConfirm}
                      onChange={(e) => setResetCustomConfirm(e.target.value)}
                      placeholder="Confirm password"
                      className="w-full border rounded-lg px-3 py-2 text-sm font-mono dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    />
                  </div>
                )}
              </div>
            </label>
          </div>
          {resetError && (
            <p className="text-sm text-red-500 dark:text-red-300 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded mb-3">{resetError}</p>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={closeResetModal} className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
              Cancel
            </button>
            <ActionButton onAction={() => confirmReset()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg" pendingLabel="Resetting…">
              Reset
            </ActionButton>
          </div>
        </Modal>
      )}

      {/* Offboard confirm */}
      {offboardTarget && (
        <Modal
          title={`Offboard ${offboardTarget.sam_account_name}`}
          onClose={() => setOffboardTarget(null)}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
            Select offboarding steps. A new random password will be generated and shown to you once.
          </p>
          <div className="space-y-2 mb-4">
            {(
              [
                ["disable_account", "Disable AD account"],
                ["reset_password", "Reset password (random, force change at logon)"],
                ["revoke_sessions", "Revoke active sessions / Kerberos tickets"],
                ["strip_group_memberships", "Remove from all non-primary groups"],
                ["move_to_disabled_ou", 'Move to "Disabled Users" OU'],
              ] as [keyof OffboardOptions, string][]
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!offboardOpts[key]}
                  onChange={(e) => setOffboardOpts({ ...offboardOpts, [key]: e.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>

          {/* Combined offboard: matched Microsoft 365 account (by UPN). */}
          {m365Match ? (
            <div className="mb-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3">
              <div className="text-xs font-semibold text-indigo-800 dark:text-indigo-200 mb-1">
                ☁ Also offboard Microsoft 365 — {m365Match.upn}
              </div>
              <div className="space-y-1.5">
                {(
                  [
                    ["disable", "Block sign-in"],
                    ["revoke_sessions", "Revoke active sessions"],
                    ["reset_mfa", "Reset (clear) MFA methods"],
                    ["remove_licenses", "Remove all licenses"],
                  ] as [keyof typeof m365Off, string][]
                ).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!m365Off[key]}
                      onChange={(e) => setM365Off({ ...m365Off, [key]: e.target.checked })}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {m365Match.on_premises_sync_enabled && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                  Hybrid user — sign-in is blocked in AD (authoritative); the cloud steps above
                  still revoke sessions, clear MFA, and free licenses.
                </p>
              )}
            </div>
          ) : (
            offboardTarget?.user_principal_name && (
              <p className="text-[11px] text-gray-400 mb-4">
                No matching Microsoft 365 account found for this UPN — AD-only offboard.
              </p>
            )
          )}

          <textarea
            placeholder="Optional note for the audit log…"
            value={offboardOpts.note || ""}
            onChange={(e) => setOffboardOpts({ ...offboardOpts, note: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 text-sm mb-4 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setOffboardTarget(null)}
              className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Cancel
            </button>
            <ActionButton onAction={() => confirmOffboard()} variant="raw" className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg" pendingLabel="Working…">
              Offboard user
            </ActionButton>
          </div>
        </Modal>
      )}

      {/* One-shot password reveal */}
      {passwordResult && (
        <Modal
          title={`${passwordResult.action} — password generated`}
          onClose={() => setPasswordResult(null)}
        >
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            New password for <strong>{passwordResult.sam}</strong>. This will only be shown once —
            copy it now.
          </p>
          <div className="bg-gray-900 text-green-300 font-mono text-base rounded-lg px-3 py-3 select-all break-all mb-3">
            {passwordResult.password}
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => navigator.clipboard?.writeText(passwordResult.password)}
              className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Copy
            </button>
            <button
              onClick={() => setPasswordResult(null)}
              className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
            >
              I've saved it
            </button>
          </div>
        </Modal>
      )}

      {onboardOpen && customerId && (
        <OnboardUserModal
          customerId={customerId}
          onClose={() => setOnboardOpen(false)}
          onRefresh={() => {
            loadUsers();
            loadAudit();
          }}
        />
      )}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose: _onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** One attribute. A dash means empty AT THE SOURCE, which is worth saying: an
 *  unexplained blank is what made this look like a sync failure. */
function Attr({
  label,
  value,
  wide,
}: {
  label: string;
  value?: string | null;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "sm:col-span-2 lg:col-span-3" : undefined}>
      <span className="text-gray-400 dark:text-gray-500">{label}: </span>
      {value ? (
        <span className="text-gray-800 dark:text-gray-200 break-all">{value}</span>
      ) : (
        <span className="text-gray-300 dark:text-gray-600">—</span>
      )}
    </div>
  );
}

/** The OU a user sits in, read off the DN. The full DN is noise; the container
 *  is the part anybody actually wants. */
function ouOf(dn?: string | null): string | null {
  if (!dn) return null;
  const parts = dn.split(",").filter((p) => /^(OU|CN)=/i.test(p.trim()));
  // Drop the leaf (the user's own CN) and reverse so it reads top-down.
  const containers = parts.slice(1).reverse().map((p) => p.trim().replace(/^(OU|CN)=/i, ""));
  return containers.length ? containers.join(" / ") : null;
}
